import React, { useState, useEffect, useMemo } from "react";
import { GridColumn as Column, getSelectedState } from "@progress/kendo-react-grid";
import { Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import { LoadingPanel } from "../../components/loadingPanel";
import { HeaderPage } from "../base/headerPage";
import { columnMenuProps } from "../../components/customColumnMenu";
import { applyFormat, executeGetColumnOrderInfo, applyDateFormat, getfilterDataString } from "../../utils/utils";
import { getter } from "@progress/kendo-react-common";
import { DialogComponent } from "src/components/dialog";
import { DELETE_BTN_ACTIONS } from "src/constants/constants";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { updateColumnReorderAsync } from "src/lib/common-service";
import { filterBy as filterByData } from "@progress/kendo-data-query";
import DataTableV2 from "src/components/dataTableV2";
import useColumnReorder from "src/custom-hooks/useColumnReorder";
import { Skeleton } from "@progress/kendo-react-indicators";
import { POSTAPI, generateHeader } from "../../api-setup/api-helper";
import {
  GET_CONTRACT_FOR_PROVIDER,
  SELECT_MOBILE_USER_DETAILS,
  INSERT_CONTRACT_DETAILS_V2,
  GET_ALL_CONTRACT_DETAILS,
} from "../../api-setup/api-endpoints";
import { getUserCredential, getTenantCookieData } from "../../lib/cookies";
import { fetchLocationForProvider } from "./services/locationService";
import { fetchContractTypeForProvider } from "./services/contractTypeService";
import { fetchNpiDataList, fetchPositionDataList, createNpiPositionMapping } from "./services/npiPositionService";
import { fetchSpecialtyData as fetchSpecialtyDataService } from "./services/specialtyService";
import { fetchAllContractDetails } from "./services/contractDetailsService";
import { fetchTaskMasterData } from "./services/taskMasterService";
import {
  dummyHospitalData,
  dummySpecialtyData,
  dummyContractTypeData,
  dummyContractNameData,
  dummyNpiData,
  dummyPositionNameData,
  dummyEmploymentTypeData,
  dummyTierData,
  dummyThresholdUnitTypeData,
  dummyRateTypeData,
  dummyRateUnitTypeData,
  dummyPeriodTypeData,
} from "./dummyData";
import AddContract from "./AddContract";
import ContractAdd from "./ContractAdd";
import UpdateContract from "./UpdateContract";
import UploadDtu from "../user/uploadDtu";
import { capitalize } from "./services/capitalize";
import { Tooltip } from "@progress/kendo-react-tooltip";

const DATA_ITEM_KEY = "id";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

// Default constants
const DEFAULT_SORT = { order: "DESC" };
const VIRTUAL_PAGE_SIZE = 30;


// Helper functions for filter string construction (similar to provider position)
const procareGetFilterValue = (data, alias) => {
  let tempArray = [];
  data.map((item) => {
    let str = item.id ? `a$${item.id}` : `a$is_null`;
    tempArray.push(str);
    return 0;
  });
  return tempArray.toString();
};

const procareGetNullInFilter = (data, filterParamName, dataType = "string", alias = "a") => {
  if (!data || data.length === 0) return "";
  const nullKeyword = "a$is_null";
  let nullFilterStr = "";

  const idList = data.map((item) => item.id).join(",");

  if (idList.includes(nullKeyword)) {
    idList.replace(new RegExp(nullKeyword, "g"), "");
    nullFilterStr = ` AND (COALESCE(${alias}.${filterParamName}, ${
      dataType === "string" ? "''" : "0"
    }) ${dataType === "string" ? "= ''" : "= 0"})`;
  }

  return ` AND (${alias}.${filterParamName} IN (${idList}))${nullFilterStr}`;
};

const StringReadAndOnlyCell = React.memo((props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;
  const val = dataItem[field || ""];

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex} className="contract-table-cell">
      <LoadingCell props={props} val={val} />
    </td>
  );
});

const BooleanCell = React.memo((props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;
  const val = dataItem[field]; // Display the raw value (1 or 0)

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex}>
      <LoadingCell props={props} val={val} />
    </td>
  );
});

const NumberCell = React.memo((props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;
  const value = props.dataItem[props.field];
  const format = props.format;
  let displayValue = value;

  // Check if value is null, undefined, or empty string
  // Use == null to check for both null and undefined
  // Explicitly handle 0 as a valid numeric value
  let formattedValue;
  if (dataItem[field] == null || dataItem[field] === "") {
    formattedValue = "";
  } else {
    // Parse and format - explicitly handle 0 as a valid number
    const parsed = parseFloat(displayValue);
    if (!isNaN(parsed)) {
      formattedValue = applyFormat(parsed, format);
    } else {
      formattedValue = displayValue;
    }
  }

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex}>
      <LoadingCell props={props} val={formattedValue} />
    </td>
  );
});

const DateCell = React.memo((props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;
  const val =
    dataItem[field] && dataItem[field] !== "0000-00-00 00:00:00" ? applyDateFormat(dataItem[field], props.format) : "";

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex}>
      <LoadingCell props={props} val={val} />
    </td>
  );
});

const LoadingCell = (restProps) => {
  return <span title={restProps.val}>{restProps.val}</span>;
};

// Cell for individual approver (shows name only)
const ApproverCell = React.memo((props) => {
  const { ariaColumnIndex, columnIndex, dataItem, field } = props;

  const name = dataItem[field] || "";

  return (
    <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex} className="contract-table-cell">
      {name ? (
        <div
          style={{
            fontSize: "12px",
            fontWeight: "500",
            color: "#495057",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "150px",
          }}
        >
          {name}
        </div>
      ) : (
        <span
          style={{
            fontSize: "12px",
            color: "#999",
            fontStyle: "italic",
          }}
        >
          Not assigned
        </span>
      )}
    </td>
  );
});

// Cell type function - now accepts onActionClick prop
const cellType = (type, field) => {
  // Handle individual approver fields (approver1_name, approver2_name, approver3_name)
  if (field.match(/^approver[1-3]_name$/)) {
    return ApproverCell;
  }

  switch (type) {
    case "date":
      return DateCell;
    case "number":
      return NumberCell;
    case "boolean":
      return BooleanCell;
    default:
      return StringReadAndOnlyCell;
  }
};

// Frontend display overrides layered on top of the DB-driven (filter_data_master) column
// metadata. Keeps date/rate presentation consistent regardless of system_unit_type formatting.
const COLUMN_DISPLAY_OVERRIDES = {
  contract_start_date: { type: "date", format: "{0:MM/dd/yyyy}" },
  contract_end_date: { type: "date", format: "{0:MM/dd/yyyy}" },
  rate: { format: "0.00" },
};

// Map dynamic column metadata (already ordered by filter_column_order) into grid columns,
// applying display overrides and a 1-based ariaColumnIndex required by the reorder hook.
const buildContractColumns = (columData) =>
  (columData || []).map((col, index) => ({
    ...col,
    ...(COLUMN_DISPLAY_OVERRIDES[col.field] || {}),
    ariaColumnIndex: index + 1,
  }));

// Column hook - now accepts onActionClick prop
const useCol = (columns, onActionClick) => {
  return useMemo(() => {
    return columns.map((column, idx) => {
      if (column.show && column.title !== "") {
        const cellComponent = cellType(column.type, column.field, onActionClick);

        return (
          <Column
            key={idx}
            {...columnMenuProps(column)}
            sortable={column.sortable !== undefined ? column.sortable : true}
            width={"150px"}
            cell={cellComponent}
            format={column.format}
          />
        );
      }
      return null;
    });
  }, [columns, onActionClick]);
};

// Table data component
const CompData = React.memo(
  (props) => {
    const {
      isAPICalling,
      initialLoading,
      data,
      pageData,
      dataCount,
      customColumn,
      dataItemKey,
      columns,
      defaultPageSize,
      pageSize,
      setPageSize,
      noDataFound,
      isExportDataClick,
      setExportData,
      exportData,
      resetSort,
      fileName,
      reorderable,
      initialFilter,
      initialSort,
      handleColumnMenu,
      columnReorderData,
      onSelectionChange,
      getExportData,
      onClearFilter,
      handlePageChange,
      refreshClick,
      rowRender,
      module,
      originalDataCount,
      defaultPageSizevs,
      originalData,
      setPageSizevs,
      pageSizevs,
      pageDatavs,
      onActionClick,
    } = props;

    const dataTableProps = useMemo(
      () => ({
        isAPICalling,
        initialLoading,
        data,
        pageData,
        dataCount,
        customColumn,
        columns,
        dataItemKey,
        width: "auto",
        defaultPageSize,
        pageSize,
        setPageSize,
        noDataFound,
        isExportDataClick,
        setExportData: setExportData,
        exportData,
        fileName,
        reorderable,
        initialFilter,
        initialSort,
        resetSort,
        handleColumnMenu,
        rowRender: rowRender,
        columnReorderData,
        onSelectionChange: onSelectionChange,
        getExportData,
        onClearFilter: onClearFilter,
        handlePageChange: handlePageChange,
        refreshClick: refreshClick,
        gridHeight: "auto",
        module,
        originalDataCount,
        defaultPageSizevs,
        originalData,
        setPageSizevs,
        pageSizevs,
        pageDatavs,
        onActionClick,
      }),
      [
        isAPICalling,
        initialLoading,
        data,
        pageData,
        dataCount,
        customColumn,
        columns,
        dataItemKey,
        defaultPageSize,
        pageSize,
        setPageSize,
        noDataFound,
        isExportDataClick,
        setExportData,
        exportData,
        fileName,
        reorderable,
        initialFilter,
        initialSort,
        resetSort,
        handleColumnMenu,
        columnReorderData,
        onSelectionChange,
        getExportData,
        onClearFilter,
        handlePageChange,
        refreshClick,
        rowRender,
        setPageSizevs,
        pageDatavs,
        onActionClick,
      ]
    );

    return (
      <>
        <DataTableV2 {...dataTableProps} />
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.data === nextProps.data &&
      prevProps.dataCount === nextProps.dataCount &&
      prevProps.isAPICalling === nextProps.isAPICalling &&
      prevProps.isSelection === nextProps.isSelection &&
      prevProps.isExportDataClick === nextProps.isExportDataClick &&
      prevProps.exportData === nextProps.exportData &&
      prevProps.columns === nextProps.columns &&
      prevProps.onActionClick === nextProps.onActionClick
    );
  }
);

const ContractManagementDetails = (pageProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const divRef = React.createRef();
  const _ref = React.useRef(null);

  // Get user credentials for API request
  const cookiesData = getUserCredential();

  // Store provider id
  const [providerId, setProviderId] = useState(null);

  // Default page size
  const defaultPageSize = {
    skip: 0,
    recordTake: VIRTUAL_PAGE_SIZE,
  };
  const defaultPageSizevs = getTenantCookieData("record_per_page");

  // State management
  const [selectedState, setSelectedState] = useState({});
  const [isShow, setShow] = useState({
    isManageCustomType: false,
    exportCurrentVersion: false,
    exportAllVersion: false,
    dtuUpload: false,
  });

  const [filterData, setFilterData] = useState({
    isSort: false,
    isFilter: false,
    data: "",
    in_sort_column: "contract_mstr_key",
    sort_order: DEFAULT_SORT.order,
  });

  const [columnsData, setColumnsData] = useState([]);
  const [isCheckColumn, setColumnCheck] = useState(false);
  const [hospitalValue, setHospitalValue] = useState([]);
  const [specialtyValue, setSpecialtyValue] = useState([]);
  const [contractTypeValue, setContractTypeValue] = useState([]);
  const [contractNameValue, setContractNameValue] = useState([]);
  const [npiValue, setNpiValue] = useState([]);
  const [positionValue, setPositionValue] = useState([]);
  const [editData, setEditData] = useState({
    isEdit: false,
    data: "",
  });

  const [filterField, setFilterField] = useState({
    logic: "and",
    filters: [],
  });

  const [sortField, setSortField] = useState([]);
  const [mode, setMode] = useState("Default");
  const [resetSort, setResetSort] = useState(false);
  const [pageData, setPage] = useState({
    skip: 0,
    take: defaultPageSize.recordTake,
  });

  const [pageSize, setPageSize] = useState(defaultPageSizevs);
  const [exportDataClick, setExportDataClick] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [deleteSelectedIds, setDeleteSelectedIds] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isSearchClick, setSearchClick] = useState(false);
  const [isPageApplied, setisPageApplied] = useState(false);
  const [loadState, setLoadState] = useState({
    isUploadState: false,
  });
  const [selectedRowId, setSelectedRowId] = useState("");
  const [searchFields, setSearchFields] = useState({});
  const [isAdd, setAdd] = useState(false);
  const [isSelection, setSelection] = useState(false);
  const [pageDatavs, setPagevs] = useState({
    skip: 0,
    take: defaultPageSizevs.recordTake,
  });
  const [gridData, setGridData] = useState([]);
  const [isDTUUploadReload, setDtUUploadReload] = useState(false);

  // Loading state for column reorder operations
  const [loadingState, setLoadingState] = useState({
    updateColumn: false,
  });

  // Filter state management
  const [hospitalDataFields, setHospitalDataFields] = useState({
    hospitalData: [],
    hospitalDataValue: [{ text: "Select All", id: "Select All" }],
    filterValue: "",
    search: "",
    isHospitalDataLoading: true,
  });

  // Store original hospital data for filter reset
  const [originalHospitalData, setOriginalHospitalData] = useState([]);

  const [specialtyDataFields, setSpecialtyDataFields] = useState({
    specialtyData: [],
    specialtyDataValue: [{ text: "Select All", id: "Select All" }],
    filterValue: "",
    search: "",
    isSpecialtyDataLoading: true,
  });

  // Store original specialty data for filter reset
  const [originalSpecialtyData, setOriginalSpecialtyData] = useState([]);

  // Store location data for contract type fetching
  const [locationData, setLocationData] = useState([]);

  const [contractTypeDataFields, setContractTypeDataFields] = useState({
    contractTypeData: [],
    contractTypeDataValue: [{ text: "Select All", id: "Select All" }],
    filterValue: "",
    search: "",
    isContractTypeDataLoading: true,
  });

  // Store original contract type data for filter reset
  const [originalContractTypeData, setOriginalContractTypeData] = useState([]);

  // Store original contract name data for filter reset
  const [originalContractNameData, setOriginalContractNameData] = useState([]);

  // Store original NPI data for filter reset
  const [originalNpiData, setOriginalNpiData] = useState([]);

  // Store original position name data for filter reset
  const [originalPositionNameData, setOriginalPositionNameData] = useState([]);

  // Store NPI/Position mapping data
  const [npiPositionRawData, setNpiPositionRawData] = useState([]);
  const [npiPositionMap, setNpiPositionMap] = useState({ npiToPosition: new Map(), positionToNpi: new Map() });

  // Store complete contract name objects with all information
  const [completeContractNamesData, setCompleteContractNamesData] = useState([]);

  // Store contract type response data
  const [contractTypeResponseData, setContractTypeResponseData] = useState([]);

  // Fetch provider id from API
  const fetchProviderId = async () => {
    if (!cookiesData.secretToken) return null;

    try {
      const apiHeaders = generateHeader();

      const requestBody = {
        in_tenant_id: cookiesData.out_tenant_id,
        in_user_id: cookiesData.out_user_id,
        in_sort_number: 1,
        in_sort_order: "ASC",
      };

      const response = await POSTAPI(false, SELECT_MOBILE_USER_DETAILS, null, apiHeaders, requestBody, dispatch);

      if (response && response.data && response.data.out_parameters) {
        const { out_ret_code, out_error_msg } = response.data.out_parameters;
        const rowResult = response.data.row_result;

        if (out_ret_code === 0) {
          if (rowResult && Array.isArray(rowResult) && rowResult.length > 0) {
            const providerIdFromAPI = rowResult[0].provider_mstr_key;
            setProviderId(providerIdFromAPI);
            return providerIdFromAPI;
          }
        } else {
          console.error("Error fetching provider id:", out_error_msg);
        }
      }
    } catch (error) {
      console.error("Error fetching provider id:", error);
    }
  };

  const [contractNameDataFields, setContractNameDataFields] = useState({
    contractNameData: [],
    contractNameDataValue: [{ text: "Select All", id: "Select All" }],
    filterValue: "",
    search: "",
    isContractNameDataLoading: true,
  });

  const [npiDataFields, setNpiDataFields] = useState({
    npiData: dummyNpiData,
    npiDataValue: [{ text: "Select All", id: "Select All" }],
    filterValue: "",
    search: "",
    isNpiDataLoading: false,
  });

  const [positionNameDataFields, setPositionNameDataFields] = useState({
    positionNameData: dummyPositionNameData,
    positionNameDataValue: [{ text: "Select All", id: "Select All" }],
    filterValue: "",
    search: "",
    isPositionNameDataLoading: false,
  });

  // Sample data for the table
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]); // Store original unfiltered data
  const [dataCount, setDataCount] = useState(0);
  const [isAPICalling, setIsAPICalling] = useState(true);
  const [noDataFound, setNoDataFound] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Columns definition (populated dynamically from filter_data_master via the API; see buildContractColumns)
  const [columns, setColumns] = useState([]);

  // Canonical full column set used as the reference for the column reorder hook;
  // set once when the dynamic columns first load.
  const [originalColumns, setOriginalColumns] = useState([]);

  // Initialize column reorder hook
  const { columnReorderData } = useColumnReorder(
    cookiesData,
    setColumnsData,
    "contract-management", // module name
    "procare_system", // role/database name
    originalColumns, // original columns reference
    setLoadingState, // loading state setter
    "" // parent module (empty string for none)
  );

  // Memoized data with selection state
  const memoizedData = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      })),
    [data, selectedState]
  );

  // Clear selection state when returning to default mode
  useEffect(() => {
    if (mode === "Default") {
      setSelectedState({});
      setSelectedRowId("");
      setDeleteSelectedIds([]);
    }
  }, [mode]);

  // Update gridData when memoizedData changes (virtual scrolling)
  useEffect(() => {
    const selobj = selectedState;
    if (
      (pageData.skip === 0 || filterData.isFilter || filterData.isSort || isPageApplied) &&
      Object.keys(selobj).length === 0
    ) {
      setGridData(memoizedData.slice(0, VIRTUAL_PAGE_SIZE));
      setisPageApplied(false);
    } else {
      setGridData(memoizedData.slice(pageData.skip, pageData.skip + pageData.take));
    }
  }, [memoizedData]); // Runs only when memoizedData changes

  // Reset pageData when returning from edit mode to default mode
  useEffect(() => {
    if (mode === "Default" && pageData.skip !== 0) {
      setPage({
        skip: 0,
        take: defaultPageSize.recordTake,
      });
      setPagevs({
        skip: 0,
        take: defaultPageSizevs.recordTake,
      });
      setGridData(memoizedData.slice(0, VIRTUAL_PAGE_SIZE));
    }
  }, [mode]);

  // Fetch location data for provider
  const handleFetchLocationForProvider = async () => {
    try {
      setHospitalDataFields((prev) => ({ ...prev, isHospitalDataLoading: true }));

      const result = await fetchLocationForProvider(cookiesData, providerId, dispatch);

      if (result.success) {
        // Update hospital filter with real data
        setLocationData(result.locationData); // Store raw location data for contract type fetching
        setOriginalHospitalData(result.transformedData);
        setHospitalDataFields((prev) => ({
          ...prev,
          hospitalData: result.transformedData,
          isHospitalDataLoading: false,
        }));
      } else {
        console.error("Failed to fetch location for provider:", result.error);
        // Fallback to empty array if API fails
        setLocationData([]); // Clear location data
        setHospitalDataFields((prev) => ({
          ...prev,
          hospitalData: [],
          isHospitalDataLoading: false,
        }));
      }
    } catch (error) {
      console.error("Error fetching hospital data:", error);
      // Fallback to empty array if API fails
      setLocationData([]); // Clear location data
      setHospitalDataFields((prev) => ({
        ...prev,
        hospitalData: [],
        isHospitalDataLoading: false,
      }));
    }
  };

  const handleFetchSpecialty = async () => {
    try {
      setSpecialtyDataFields((prev) => ({ ...prev, isSpecialtyDataLoading: true }));

      const result = await fetchSpecialtyDataService(cookiesData, dispatch);

      if (result.success) {
        setOriginalSpecialtyData(result.specialtyData);
        setSpecialtyDataFields((prev) => ({
          ...prev,
          specialtyData: result.specialtyData,
          isSpecialtyDataLoading: false,
        }));
      } else {
        console.error("Failed to fetch specialty data:", result.error);
        setSpecialtyDataFields((prev) => ({ ...prev, specialtyData: [], isSpecialtyDataLoading: false }));
      }
    } catch (error) {
      console.error("Error fetching specialty data:", error);
      setSpecialtyDataFields((prev) => ({ ...prev, specialtyData: [], isSpecialtyDataLoading: false }));
    }
  };

  // Fetch contract type data from backend
  const handleFetchContractTypeForProvider = async () => {
    try {
      setContractTypeDataFields((prev) => ({ ...prev, isContractTypeDataLoading: true }));

      const result = await fetchContractTypeForProvider(cookiesData, providerId, dispatch);

      if (result.success) {
        setOriginalContractTypeData(result.contractTypesForFilter);
        setContractTypeDataFields((prev) => ({
          ...prev,
          contractTypeData: result.contractTypesForFilter,
          isContractTypeDataLoading: false,
        }));

        // Store the raw contract type response data for contract details fetching
        setContractTypeResponseData(result.rawResponseData);
      } else {
        console.error("Failed to fetch contract type for provider:", result.error);
        setContractTypeDataFields((prev) => ({
          ...prev,
          contractTypeData: [],
          isContractTypeDataLoading: false,
        }));
      }
    } catch (error) {
      console.error("Error fetching contract type data:", error);
      setContractTypeDataFields((prev) => ({
        ...prev,
        contractTypeData: [],
        isContractTypeDataLoading: false,
      }));
    }
  };

  // Fetch NPI data
  const handleFetchNpiData = async () => {
    try {
      setNpiDataFields((prev) => ({ ...prev, isNpiDataLoading: true }));

      const result = await fetchNpiDataList(null, null, cookiesData, dispatch, history, true);

      setOriginalNpiData(result.transformedData);
      setNpiDataFields((prev) => ({
        ...prev,
        npiData: result.transformedData,
        isNpiDataLoading: false,
      }));
      // Store raw data for mapping
      setNpiPositionRawData(result.rawData);
    } catch (error) {
      console.error("Error fetching NPI data:", error);
      setOriginalNpiData([]);
      setNpiDataFields((prev) => ({
        ...prev,
        npiData: [],
        isNpiDataLoading: false,
      }));
    }
  };

  // Fetch position data
  const handleFetchPositionData = async () => {
    try {
      setPositionNameDataFields((prev) => ({ ...prev, isPositionNameDataLoading: true }));

      const result = await fetchPositionDataList(null, null, cookiesData, dispatch, history, true);

      setOriginalPositionNameData(result.transformedData);
      setPositionNameDataFields((prev) => ({
        ...prev,
        positionNameData: result.transformedData,
        isPositionNameDataLoading: false,
      }));
      // Raw data for position is already stored when fetching NPI data
      // (same API endpoint returns both NPI and position data)
    } catch (error) {
      console.error("Error fetching position data:", error);
      setOriginalPositionNameData([]);
      setPositionNameDataFields((prev) => ({
        ...prev,
        positionNameData: [],
        isPositionNameDataLoading: false,
      }));
    }
  };

  // Fetch contract names (tasks) from backend
  const fetchContractNames = async () => {
    try {
      setContractNameDataFields((prev) => ({ ...prev, isContractNameDataLoading: true }));

      const result = await fetchTaskMasterData(cookiesData, dispatch);

      if (result.success) {
        // Use transformedData which already has task_mstr_key as id
        // Group by name and collect all task_mstr_keys for filtering
        const contractNamesForFilter = result.transformedData || [];

        // Store all contract name information (complete objects) in a separate state
        setCompleteContractNamesData(result.taskMasterData);
        setOriginalContractNameData(contractNamesForFilter);
        setContractNameDataFields((prev) => ({
          ...prev,
          contractNameData: contractNamesForFilter,
          isContractNameDataLoading: false,
        }));
      } else {
        console.error("Failed to fetch contract names:", result.error);
        setContractNameDataFields((prev) => ({
          ...prev,
          contractNameData: [],
          isContractNameDataLoading: false,
        }));
      }
    } catch (error) {
      console.error("Error fetching contract names:", error);
      setContractNameDataFields((prev) => ({
        ...prev,
        contractNameData: [],
        isContractNameDataLoading: false,
      }));
    }
  };

  // Get API call request with filters
  const getAPICallReq = () => {
    let apiCallData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: filterData.isFilter ? filterData.data : null,
      in_sort_column: filterData.isSort ? filterData.in_sort_column : "contract_mstr_key",
      in_sort_order: filterData.isSort ? filterData.sort_order : DEFAULT_SORT.order,
      in_page_row_offset: getPageSkip(),
      in_page_rows: getPageTake(),
      in_export_flag: 0,
      in_hospital_filter:
        searchFields?.hospitalValue && searchFields.hospitalValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetNullInFilter(
              searchFields.hospitalValue.filter((x) => x.text !== "Select All"),
              "location_mstr_key",
              "number",
              "lm"
            )
          : null,
      in_specialty_filter:
        searchFields?.specialtyValue && searchFields.specialtyValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetNullInFilter(
              searchFields.specialtyValue.filter((x) => x.text !== "Select All"),
              "specialty_mstr_key",
              "number",
              "sm"
            )
          : null,
      in_contract_type_filter:
        searchFields?.contractTypeValue &&
        searchFields.contractTypeValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetNullInFilter(
              searchFields.contractTypeValue.filter((x) => x.text !== "Select All"),
              "task_category_mstr_key",
              "number",
              "tcm"
            )
          : null,
      in_contract_name_filter:
        searchFields?.contractNameValue &&
        searchFields.contractNameValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetNullInFilter(
              searchFields.contractNameValue.filter((x) => x.text !== "Select All"),
              "task_mstr_key",
              "number",
              "tm"
            )
          : null,
      in_npi_filter:
        searchFields?.npiValue && searchFields.npiValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetFilterValue(
              searchFields.npiValue.filter((x) => x.text !== "Select All"),
              "pm"
            )
          : null,
      in_position_filter:
        searchFields?.positionValue && searchFields.positionValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetNullInFilter(
              searchFields.positionValue.filter((x) => x.text !== "Select All"),
              "org_position_key",
              "number",
              "op"
            )
          : null,
    };
    return apiCallData;
  };

  // Fetch contract details with server-side pagination
  const fetchContractDetails = async (
    offset = 0,
    rows = 500,
    filter = null,
    sortCol = "contract_mstr_key",
    sortOrd = "DESC",
    apiCallReq = null
  ) => {
    try {
      setIsAPICalling(true);
      if (offset === 0) {
        setInitialLoading(true);
      }

      // Use provided apiCallReq or build from current state
      const requestData = apiCallReq || {
        in_tenant_id: cookiesData.out_tenant_id,
        in_filter: filter,
        in_sort_column: sortCol,
        in_sort_order: sortOrd,
        in_page_row_offset: offset,
        in_page_rows: rows,
        in_export_flag: 0,
        in_hospital_filter: null,
        in_specialty_filter: null,
        in_contract_type_filter: null,
        in_contract_name_filter: null,
        in_npi_filter: null,
        in_position_filter: null,
      };

      // Fetch contract details with pagination and filter parameters
      const result = await fetchAllContractDetails(
        cookiesData,
        dispatch,
        requestData.in_page_row_offset,
        requestData.in_page_rows,
        requestData.in_filter,
        requestData.in_sort_column,
        requestData.in_sort_order,
        0, // export_flag = 0 for pagination
        requestData.in_hospital_filter,
        requestData.in_specialty_filter,
        requestData.in_contract_type_filter,
        requestData.in_contract_name_filter,
        requestData.in_npi_filter,
        requestData.in_position_filter
      );

      if (result.success) {
        // Initialize dynamic columns from filter_data_master on first successful load
        if (result.columData && result.columData.length > 0 && originalColumns.length === 0) {
          const mappedColumns = buildContractColumns(result.columData);
          setColumns(mappedColumns);
          setOriginalColumns(mappedColumns);
        }
        // Update table with current page data
        setData(result.data); // Only current page
        setOriginalData([...result.data]); // Keep for compatibility
        setDataCount(result.totalRows); // Total from server
        setNoDataFound(result.data.length === 0);
      } else {
        console.error("Failed to fetch contract details:", result.error);
        setData([]);
        setOriginalData([]);
        setDataCount(0);
        setNoDataFound(true);
      }

      if (offset === 0) {
        setInitialLoading(false);
      }
    } catch (error) {
      console.error("Error fetching contract details:", error);
      setData([]);
      setOriginalData([]);
      setDataCount(0);
      setNoDataFound(true);
      if (offset === 0) {
        setInitialLoading(false);
      }
    } finally {
      setIsAPICalling(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchProviderId();
  }, []);

  // Fetch hospital and specialty data after provider id is loaded
  useEffect(() => {
    const initializeData = async () => {
      setIsAPICalling(true);
      setInitialLoading(true);

      try {
        // Fetch hospital data first
        await handleFetchLocationForProvider();

        // Fetch specialty data
        await handleFetchSpecialty();

        // Fetch contract type data
        await handleFetchContractTypeForProvider();

        // Fetch contract names
        await fetchContractNames();

        // Fetch contract details from API
        await fetchContractDetails();
      } catch (error) {
        console.error("Error initializing contract data:", error);
        // Set empty data if initialization fails
        setData([]);
        setDataCount(0);
        setColumns([...columns]);
        // Don't set gridData here, let the useEffect handle it
      } finally {
        setIsAPICalling(false);
        setInitialLoading(false);
      }
    };

    initializeData();
  }, [providerId]);

  // Fetch contract details after contract types are available
  useEffect(() => {
    if (originalContractTypeData.length > 0) {
      fetchContractDetails();
    }
  }, [originalContractTypeData, providerId]);

  // Fetch position data independent of provider id
  useEffect(() => {
    handleFetchNpiData();
    handleFetchPositionData();
  }, []);

  // Create NPI/Position mapping when raw data is available
  useEffect(() => {
    if (npiPositionRawData && npiPositionRawData.length > 0) {
      const mapping = createNpiPositionMapping(npiPositionRawData);
      setNpiPositionMap(mapping);
    }
  }, [npiPositionRawData]);

  // Reset exportDataClick after export data is set (export is triggered)
  useEffect(() => {
    if (exportData.length > 0 && exportDataClick) {
      // Small delay to ensure export has been triggered
      setTimeout(() => {
        setExportDataClick(false);
      }, 100);
    }
  }, [exportData, exportDataClick]);

  useEffect(() => {
    if (columns.length > 0) {
      setColumnsData(columns);
      setGridColumns(columns);
    }
  }, [columns]);

  // Note: With server-side pagination, we don't need client-side slicing
  // Data is already paginated from the server
  // useEffect(() => {
  //   if (data.length > 0) {
  //     setGridData(data);
  //   }
  // }, [data]);

  // Filter change handlers
  const handleChangeDropdownValue = (e, name, setDataFields, dataList, valueData, dataFields) => {
    if (valueData && valueData.filter((x) => x.text === "Select All").length > 0) {
      let index = e.value.findIndex((x) => x.text === "Select All");
      e.value.splice(index, 1);
    } else if (e.value && e.value.filter((x) => x.text === "Select All").length > 0 && e.value.length > 1) {
      e.value = [];
      e.value.push({ text: "Select All", id: "Select All" });
    }
    if (e.value.length === 0) {
      e.value.push({ text: "Select All", id: "Select All" });
    }

    handleOnChange(e.value, name, setDataFields, dataList, dataFields);
  };

  // Auto-select positions when NPI is selected
  const handleNpiChange = (selectedNpis) => {
    // Get selected NPI values (excluding "Select All")
    const selectedNpiValues = selectedNpis.filter((item) => item.id !== "Select All").map((item) => item.id); // id is the NPI value (empty string for "(Empty Value)")

    // Find corresponding position keys
    const correspondingPositionKeys = [];
    selectedNpiValues.forEach((npiValue) => {
      const positionKey = npiPositionMap.npiToPosition.get(npiValue);
      if (positionKey) {
        correspondingPositionKeys.push(positionKey);
      }
    });

    // Auto-select corresponding positions
    if (correspondingPositionKeys.length > 0) {
      const positionsToSelect = originalPositionNameData.filter((pos) => correspondingPositionKeys.includes(pos.id));

      if (positionsToSelect.length > 0) {
        setPositionNameDataFields((prev) => ({
          ...prev,
          positionNameDataValue: positionsToSelect,
        }));
      }
    } else if (selectedNpis.length === 1 && selectedNpis[0].text === "Select All") {
      // If "Select All" is selected in NPI, select "Select All" in Position
      setPositionNameDataFields((prev) => ({
        ...prev,
        positionNameDataValue: [{ text: "Select All", id: "Select All" }],
      }));
    }
  };

  // Auto-select NPIs when position is selected
  const handlePositionChange = (selectedPositions) => {
    // Get selected position keys (excluding "Select All")
    const selectedPositionKeys = selectedPositions.filter((item) => item.id !== "Select All").map((item) => item.id);

    // Find corresponding NPI values
    const correspondingNpiValues = [];
    selectedPositionKeys.forEach((positionKey) => {
      const npiValue = npiPositionMap.positionToNpi.get(positionKey);
      if (npiValue !== undefined) {
        // Check for undefined (allows empty string)
        correspondingNpiValues.push(npiValue);
      }
    });

    // Auto-select corresponding NPIs
    if (correspondingNpiValues.length > 0) {
      const npisToSelect = originalNpiData.filter((npi) => correspondingNpiValues.includes(npi.id));

      if (npisToSelect.length > 0) {
        setNpiDataFields((prev) => ({
          ...prev,
          npiDataValue: npisToSelect,
        }));
      }
    } else if (selectedPositions.length === 1 && selectedPositions[0].text === "Select All") {
      // If "Select All" is selected in Position, select "Select All" in NPI
      setNpiDataFields((prev) => ({
        ...prev,
        npiDataValue: [{ text: "Select All", id: "Select All" }],
      }));
    }
  };

  const handleOnChange = (e, name, setDataFields, dataList, dataFields) => {
    let dataValue = [];
    if (e.length === 1 && e.some((x) => x.text === "Select All")) {
      dataValue.push({ text: "Select All", id: "Select All" });
    } else {
      dataValue = [...e];
    }
    setDataFields({
      ...dataFields,
      [name]: dataValue,
    });

    if (name === "hospitalDataValue") {
      setHospitalValue(e);
    } else if (name === "specialtyDataValue") {
      setSpecialtyValue(e);
    } else if (name === "contractTypeDataValue") {
      setContractTypeValue(e);
    } else if (name === "contractNameDataValue") {
      setContractNameValue(e);
    } else if (name === "npiDataValue") {
      setNpiValue(e);
    } else if (name === "positionNameDataValue") {
      setPositionValue(e);
    }
  };

  const filterChange = (event, setDataFields, moduleData, key) => {
    let dataValue = filterBy(moduleData.slice(), event.filter);
    setDataFields((prev) => ({
      ...prev,
      [`${key}`]: dataValue,
      filterValue: event.filter.value,
      search: event.filter,
    }));
  };

  const onOpenChange = (setDataFields, moduleData, key, dataFields, dataFieldsValue) => {
    let dataValue =
      dataFields.filterValue && dataFields.filterValue !== ""
        ? filterBy(dataFieldsValue.slice(), dataFields.search)
        : moduleData;
    setDataFields((prev) => ({
      ...prev,
      [`${key}`]: dataValue,
    }));
  };

  // Callback for adding new contract
  const handleAddContract = async (contractData) => {
    try {
      // Refresh data from API after contract is added with current filters
      const apiCallData = getAPICallReq();

      await fetchContractDetails(
        pageDatavs.skip,
        pageDatavs.take,
        getInFilter(),
        getInSortColumn(),
        getInSortOrder(),
        apiCallData
      );

      // Clear selected state
      setSelectedState({});
      setSelectedRowId("");
      setDeleteSelectedIds([]);

      setMode("Default");
    } catch (error) {
      console.error("Error refreshing data after adding contract:", error);
      alert("Failed to refresh data after adding contract. Please try again.");
    }
  };

  // Callback for updating contract
  const handleUpdateContract = async (contractData) => {
    try {
      // Refresh data from API after contract is updated with current filters
      const apiCallData = getAPICallReq();

      await fetchContractDetails(
        pageDatavs.skip,
        pageDatavs.take,
        getInFilter(),
        getInSortColumn(),
        getInSortOrder(),
        apiCallData
      );

      // Clear selected state to remove red background
      setSelectedState({});
      setSelectedRowId("");
      setDeleteSelectedIds([]);

      setMode("Default");
      setIsUpdate(true);
    } catch (error) {
      console.error("Error refreshing data after updating contract:", error);
      alert("Failed to refresh data after updating contract. Please try again.");
    }
  };

  // Search functionality - server-side filtering
  const onSearchClick = async () => {
    setSearchClick(true);
    setFilterField({ logic: "and", filters: [] });
    setSortField([]);
    // Reset grid filters and sorting
    setResetSort(true);
    setTimeout(() => {
      setResetSort(false);
    }, 100);
    setPageSize(defaultPageSizevs);
    setPage({
      skip: 0,
      take: defaultPageSize.recordTake,
    });
    setPagevs({
      skip: 0,
      take: defaultPageSizevs.recordTake,
    });
    setDataCount(0);
    setSelectedRowId("");
    setDeleteSelectedIds([]);
    setSelectedState({});
    setData([]);
    setColumns([]);
    setFilterData({
      isSort: false,
      isFilter: false,
      data: "",
      in_sort_column: "contract_mstr_key",
      sort_order: DEFAULT_SORT.order,
    });
    setMode("Default");
    setIsAPICalling(true);

    // Capture current filter values
    const currentHospitalValue = hospitalDataFields.hospitalDataValue;
    const currentSpecialtyValue = specialtyDataFields.specialtyDataValue;
    const currentContractTypeValue = contractTypeDataFields.contractTypeDataValue;
    const currentContractNameValue = contractNameDataFields.contractNameDataValue;
    const currentNpiValue = npiDataFields.npiDataValue;
    const currentPositionValue = positionNameDataFields.positionNameDataValue;

    // Update the separate value states with captured values (these are used for filtering)
    setHospitalValue(currentHospitalValue);
    setSpecialtyValue(currentSpecialtyValue);
    setContractTypeValue(currentContractTypeValue);
    setContractNameValue(currentContractNameValue);
    setNpiValue(currentNpiValue);
    setPositionValue(currentPositionValue);

    // Store filter values in searchFields for use in getAPICallReq
    setSearchFields({
      ...searchFields,
      hospitalValue: currentHospitalValue,
      specialtyValue: currentSpecialtyValue,
      contractTypeValue: currentContractTypeValue,
      contractNameValue: currentContractNameValue,
      npiValue: currentNpiValue,
      positionValue: currentPositionValue,
    });

    // Build API request with filters
    const apiCallData = {
      in_tenant_id: cookiesData.out_tenant_id,
      in_filter: null,
      in_sort_column: "contract_mstr_key",
      in_sort_order: DEFAULT_SORT.order,
      in_page_row_offset: 0,
      in_page_rows: defaultPageSizevs.recordTake,
      in_export_flag: 0,
      in_hospital_filter:
        currentHospitalValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetNullInFilter(
              currentHospitalValue.filter((x) => x.text !== "Select All"),
              "location_mstr_key",
              "number",
              "lm"
            )
          : null,
      in_specialty_filter:
        currentSpecialtyValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetNullInFilter(
              currentSpecialtyValue.filter((x) => x.text !== "Select All"),
              "specialty_mstr_key",
              "number",
              "sm"
            )
          : null,
      in_contract_type_filter:
        currentContractTypeValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetNullInFilter(
              currentContractTypeValue.filter((x) => x.text !== "Select All"),
              "task_category_mstr_key",
              "number",
              "tcm"
            )
          : null,
      in_contract_name_filter:
        currentContractNameValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetNullInFilter(
              currentContractNameValue.filter((x) => x.text !== "Select All"),
              "task_mstr_key",
              "number",
              "tm"
            )
          : null,
      in_npi_filter:
        currentNpiValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetFilterValue(
              currentNpiValue.filter((x) => x.text !== "Select All"),
              "pm"
            )
          : null,
      in_position_filter:
        currentPositionValue.filter((x) => x.text !== "Select All").length > 0
          ? procareGetNullInFilter(
              currentPositionValue.filter((x) => x.text !== "Select All"),
              "org_position_key",
              "number",
              "op"
            )
          : null,
    };

    // Call API with filters
    await fetchContractDetails(
      0,
      defaultPageSizevs.recordTake,
      null,
      "contract_mstr_key",
      DEFAULT_SORT.order,
      apiCallData
    );
  };

  // Page change handler - server-side pagination
  const handlePageChange = async (event) => {
    const pageData = event.page;
    const vskip = pageData.skip;
    const vtake = pageData.take;

    if (event.syntheticEvent.target.props !== undefined) {
      setisPageApplied(true);
      setPagevs({ skip: vskip, take: vtake });

      // Build API request with current filters and new pagination
      const apiCallData = getAPICallReq();
      apiCallData.in_page_row_offset = vskip;
      apiCallData.in_page_rows = vtake;

      // Explicitly set sorting parameters for consistency
      apiCallData.in_sort_column = getInSortColumn();
      apiCallData.in_sort_order = getInSortOrder();

      // Make API call with pagination and filter params
      await fetchContractDetails(vskip, vtake, getInFilter(), getInSortColumn(), getInSortOrder(), apiCallData);

      if (_ref.current) {
        const scrollableElement = _ref.current.querySelector(".k-grid-content");
        if (scrollableElement) {
          scrollableElement.scrollTop = 0;
        }
      }
    } else {
      setPage({ skip: vskip, take: vtake });
      setGridData(memoizedData.slice(vskip, vskip + vtake));
    }
  };

  // Refresh functionality - reload current page with filters
  const onRefreshClick = () => {
    // Reload data with current filters and sorting preserved
    const apiCallData = getAPICallReq();
    apiCallData.in_filter = getInFilter();
    apiCallData.in_sort_column = getInSortColumn();
    apiCallData.in_sort_order = getInSortOrder();

    fetchContractDetails(pageDatavs.skip, pageDatavs.take, getInFilter(), getInSortColumn(), getInSortOrder(), apiCallData);
  };

  // Clear filter functionality - reload first page without column filters (preserves dropdown filters)
  const onClearFilter = () => {
    // Only clear column filters
    setFilterField({ logic: "and", filters: [] });
    setSortField([]);
    setFilterData({
      ...filterData,
      isFilter: false,
      data: "",
    });

    // Reset pagination and reload first page with dropdown filters preserved
    setPagevs({ skip: 0, take: defaultPageSizevs.recordTake });

    // Build API request with dropdown filters preserved (via getAPICallReq)
    // getAPICallReq() reads from searchFields which contains the active dropdown filter values
    const apiCallData = getAPICallReq();
    apiCallData.in_page_row_offset = 0;
    apiCallData.in_page_rows = defaultPageSizevs.recordTake;
    apiCallData.in_filter = null; // No generic filter string
    apiCallData.in_sort_column = "contract_mstr_key";
    apiCallData.in_sort_order = DEFAULT_SORT.order;

    fetchContractDetails(0, defaultPageSizevs.recordTake, null, "contract_mstr_key", DEFAULT_SORT.order, apiCallData);

    if (_ref.current) {
      const scrollableElement = _ref.current.querySelector(".k-grid-content");
      if (scrollableElement) {
        scrollableElement.scrollTop = 0;
      }
    }
  };

  // Action click handler (for action cell clicks)
  const onActionClick = (dataItem) => {
    // Extract contract_mstr_key from id field (format: contract_mstr_key-index)
    const contractMstrKey = dataItem.id ? parseInt(dataItem.id.split("-")[0]) : null;
    const providerMstrKey = dataItem.providerId || null;
    const locationMstrKey = dataItem.location_mstr_key || null;
    const version = dataItem.version;

    setEditData({
      ...editData,
      isEdit: true,
      data: {
        contractMstrKey: contractMstrKey,
        providerId: providerMstrKey,
        locationMstrKey: locationMstrKey,
        version: version,
      },
    });
    setGridData(memoizedData.slice(0, VIRTUAL_PAGE_SIZE));
    setMode("Edit");
    setSelection(false);
  };

  // Row double click handler
  const onRowDoubleClick = (props) => {
    // Handle different props structures
    const dataItem = props.dataItem;
    // Extract contract_mstr_key from id field (format: contract_mstr_key-index)
    const contractMstrKey = dataItem.id ? parseInt(dataItem.id.split("-")[0]) : null;
    const providerMstrKey = dataItem.providerId || null;
    const locationMstrKey = dataItem.location_mstr_key || null;
    const version = dataItem.version;

    setEditData({
      ...editData,
      isEdit: true,
      data: {
        contractMstrKey: contractMstrKey,
        providerId: providerMstrKey,
        locationMstrKey: locationMstrKey,
        version: version,
      },
    });
    setGridData(memoizedData.slice(0, VIRTUAL_PAGE_SIZE));
    setMode("Edit");
    setSelection(false);
  };

  // Column menu handler
  const handleColumnMenu = async (columnMenuData, isFilter, isSort, filterObj) => {
    let filterSkip = getPageSkip();
    let filterTake = getPageTake();
    setIsAPICalling(true);

    if (isSort) {
      setSortField(columnMenuData);
    } else {
      filterSkip = 0;
      filterTake = pageSize.recordTake;
      setFilterField(filterObj);
      setPagevs({ skip: filterSkip, take: filterTake });
    }

    const filterString = isFilter ? getfilterDataString(columnMenuData, columns) : filterData.data;
    const computedIsFilter = filterString !== "";

    let sortDir = filterData.isSort && columnMenuData.length ? filterData.sort_order : DEFAULT_SORT.order;
    let sortColumn = filterData.isSort && columnMenuData.length ? filterData.in_sort_column : "contract_mstr_key"; // Default sort by contract key

    let computedIsSort = !columnMenuData.length ? false : filterData.isSort;

    if (columnMenuData[0] !== undefined && columnMenuData[0].dir !== undefined) {
      computedIsSort = true;
      sortDir = columnMenuData[0].dir.toUpperCase();
      sortColumn = columnMenuData[0].field;
    }

    setFilterData({
      ...filterData,
      isFilter: computedIsFilter,
      isSort: computedIsSort,
      data: filterString,
      in_sort_column: sortColumn,
      sort_order: sortDir,
    });

    try {
      // Build API request with the generic filter + dropdown filters
      const apiCallData = getAPICallReq();
      apiCallData.in_filter = computedIsFilter ? filterString : null;
      apiCallData.in_sort_column = sortColumn;
      apiCallData.in_sort_order = sortDir;
      apiCallData.in_page_row_offset = filterSkip;
      apiCallData.in_page_rows = filterTake;

      // Call backend API with filter and sort parameters
      await fetchContractDetails(
        filterSkip,
        filterTake,
        computedIsFilter ? filterString : null,
        sortColumn,
        sortDir,
        apiCallData
      );
    } catch (error) {
      console.error("Error applying column filter/sort:", error);
      setIsAPICalling(false);
    }

    // Scroll to top after data loads
    if (_ref.current) {
      const scrollableElement = _ref.current.querySelector(".k-grid-content");
      if (scrollableElement) {
        scrollableElement.scrollTop = 0;
      }
    }
  };

  // Selection change handler
  const onSelectionChange = (event) => {
    setSelection(false);
    const newSelectedState = getSelectedState({
      event,
      selectedState: selectedState,
      dataItemKey: DATA_ITEM_KEY,
    });

    let tempArray = [];

    Object.keys(newSelectedState).map((item) => {
      if (newSelectedState[item] === false) {
        return 0;
      }
      let obj = {};
      obj.id = item;
      tempArray.push(obj);
      return 0;
    });

    const newSelectedStateKeys = Object.keys(newSelectedState);

    const selectedKeyArr = newSelectedStateKeys.filter(function (x) {
      return newSelectedState[x] !== false;
    });

    let newData = data.map((item) => ({
      ...item,
      selected: selectedKeyArr.includes(item.id.toString()),
    }));

    setSelectedRowId(selectedKeyArr[0]);
    setData(newData);
    setSelectedState(newSelectedState);
    setDeleteSelectedIds(tempArray);
    setSelection(true);
  };

  // Utility functions
  const getPageTake = () => {
    return pageDatavs.take;
  };

  const getPageSkip = () => {
    return pageDatavs.skip;
  };

  const getInFilter = () => {
    return filterData.isFilter ? filterData.data : null;
  };

  const getInSortColumn = () => {
    return filterData.isSort ? filterData.in_sort_column : "contract_mstr_key";
  };

  const getInSortOrder = () => {
    return filterData.isSort ? filterData.sort_order : DEFAULT_SORT.order;
  };

  // Export data function - fetches all data with in_export_flag = 1
  const getExportData = async () => {
    try {
      // Get current dropdown filter values from state (not from searchFields which may be stale)
      const currentHospitalValue = hospitalDataFields.hospitalDataValue;
      const currentSpecialtyValue = specialtyDataFields.specialtyDataValue;
      const currentContractTypeValue = contractTypeDataFields.contractTypeDataValue;
      const currentContractNameValue = contractNameDataFields.contractNameDataValue;
      const currentNpiValue = npiDataFields.npiDataValue;
      const currentPositionValue = positionNameDataFields.positionNameDataValue;

      // Build base API call data with dropdown filters
      const exportRequestData = {
        in_tenant_id: cookiesData.out_tenant_id,
        in_filter: getInFilter(),
        in_sort_column: getInSortColumn(),
        in_sort_order: getInSortOrder(),
        in_page_row_offset: 0,
        in_page_rows: 1000000,
        in_export_flag: 1,
        in_hospital_filter:
          currentHospitalValue && currentHospitalValue.filter((x) => x.text !== "Select All").length > 0
            ? procareGetNullInFilter(
                currentHospitalValue.filter((x) => x.text !== "Select All"),
                "location_mstr_key",
                "number",
                "lm"
              )
            : null,
        in_specialty_filter:
          currentSpecialtyValue && currentSpecialtyValue.filter((x) => x.text !== "Select All").length > 0
            ? procareGetNullInFilter(
                currentSpecialtyValue.filter((x) => x.text !== "Select All"),
                "specialty_mstr_key",
                "number",
                "sm"
              )
            : null,
        in_contract_type_filter:
          currentContractTypeValue && currentContractTypeValue.filter((x) => x.text !== "Select All").length > 0
            ? procareGetNullInFilter(
                currentContractTypeValue.filter((x) => x.text !== "Select All"),
                "task_category_mstr_key",
                "number",
                "tcm"
              )
            : null,
        in_contract_name_filter:
          currentContractNameValue && currentContractNameValue.filter((x) => x.text !== "Select All").length > 0
            ? procareGetNullInFilter(
                currentContractNameValue.filter((x) => x.text !== "Select All"),
                "task_mstr_key",
                "number",
                "tm"
              )
            : null,
        in_npi_filter:
          currentNpiValue && currentNpiValue.filter((x) => x.text !== "Select All").length > 0
            ? procareGetFilterValue(
                currentNpiValue.filter((x) => x.text !== "Select All"),
                "pm"
              )
            : null,
        in_position_filter:
          currentPositionValue && currentPositionValue.filter((x) => x.text !== "Select All").length > 0
            ? procareGetNullInFilter(
                currentPositionValue.filter((x) => x.text !== "Select All"),
                "org_position_key",
                "number",
                "op"
              )
            : null,
      };

      // Fetch all contract details for export
      const result = await fetchAllContractDetails(
        cookiesData,
        dispatch,
        exportRequestData.in_page_row_offset,
        exportRequestData.in_page_rows,
        exportRequestData.in_filter,
        exportRequestData.in_sort_column,
        exportRequestData.in_sort_order,
        exportRequestData.in_export_flag,
        exportRequestData.in_hospital_filter,
        exportRequestData.in_specialty_filter,
        exportRequestData.in_contract_type_filter,
        exportRequestData.in_contract_name_filter,
        exportRequestData.in_npi_filter,
        exportRequestData.in_position_filter
      );

      if (result.success && result.data.length > 0) {
        // Format data for Excel export
        const formattedData = result.data.map((item) => {
          const formattedItem = { ...item };

          // Format date fields
          gridColumns.forEach((col) => {
            if (col.type === "date" && col.field) {
              const fieldValue = formattedItem[col.field];
              if (fieldValue && fieldValue !== "0000-00-00 00:00:00") {
                formattedItem[col.field] = new Date(applyDateFormat(fieldValue, col.format || "{0:MM/dd/yyyy}"));
              } else {
                formattedItem[col.field] = null;
              }
            }
            // Format boolean fields
            else if (col.type === "boolean" && col.field) {
              const fieldValue = formattedItem[col.field];
              if (fieldValue !== null && fieldValue !== undefined) {
                formattedItem[col.field] = fieldValue ? "Yes" : "No";
              }
            }
            // Format number fields
            else if (col.type === "number" && col.field) {
              const fieldValue = formattedItem[col.field];
              if (fieldValue != null && fieldValue !== "") {
                let numericValue = parseFloat(fieldValue);

                // Apply format if specified
                if (col.format) {
                  formattedItem[col.field] = applyFormat(numericValue, col.format);
                } else {
                  formattedItem[col.field] = numericValue;
                }
              } else {
                formattedItem[col.field] = null;
              }
            }
          });

          return formattedItem;
        });

        setExportData(formattedData);
      } else {
        console.error("No data to export:", result.error);
        setExportData([]);
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      setExportData([]);
    }
  };

  // Persist and apply Manage Columns changes
  useEffect(() => {
    if (isCheckColumn) {
      if (pageProps.writeAccess === 0) {
        return false;
      }
      updateColumDataAsync();
    }
  }, [isCheckColumn]);

  const updateColumDataAsync = async () => {
    try {
      const columnString = executeGetColumnOrderInfo(columnsData, "contract-management");
      const reqData = {
        in_db_name: "procare_system",
        in_tenant_id: cookiesData.out_tenant_id,
        in_filter_logical_cond: "N",
        in_filter_label_cond: "N",
        in_column_info: columnString,
      };
      const res = await updateColumnReorderAsync(reqData, dispatch, history);
      if (res) {
        setColumns(columnsData);
        setColumnCheck(false);
      } else {
        setColumnCheck(false);
      }
    } catch (err) {
      setColumnCheck(false);
    }
  };

  // Add new link component
  const AddNewLink = React.memo(() => {
    return (
      <div>
        <a
          className="cta-small"
          onClick={(e) => {
            setMode("Add");
          }}
        >
          <i className="fas fa-plus"></i> Add New Contract
        </a>
      </div>
    );
  });

  // Upload handler
  const handleUpload = (e) => {
    setShow({
      ...isShow,
      dtuUpload: !isShow.dtuUpload,
    });
    e.preventDefault();
  };

  // Refresh callback for manual upload
  const onRefreshOnManualUpload = () => {
    setDtUUploadReload(true);
    // Add any additional refresh logic here if needed
    setTimeout(() => {
      setDtUUploadReload(false);
    }, 1000);
  };

  // Header configuration
  const headerConfig = useMemo(
    () => ({
      title: "Contract Management",
      id: "contract-management-details",
      add_new_fx: AddNewLink,
      // upload_fx: handleUpload,
      action_menu: {
        export: {
          fx: () => {
            // Only allow export if data exists
            if (dataCount > 0 && data.length > 0) {
              setExportDataClick(true);
            }
          },
          caption: "Export to Excel",
          disabled: dataCount === 0 || data.length === 0,
        },
      },
      ...(pageProps.writeAccess === 1 && {
        manage_col: {
          columnsData: columnsData,
          setColumnsData: setColumnsData,
          setColumnCheck: setColumnCheck,
        },
      }),
    }),
    [dataCount, data.length, columnsData, pageProps.writeAccess]
  );

  // Custom row render function
  const customRowRender = React.useCallback((tr, props) => {
    return (
      <tr
        {...tr.props}
        onDoubleClick={() => onRowDoubleClick({ dataItem: props.dataItem, originalProps: props })}
        onClick={(e) => e.stopPropagation()}
        style={{ cursor: "default" }}
        title="Double click to edit record"
      >
        {tr.props.children}
      </tr>
    );
  }, []);

  // Columns for the table
  let CustomColumn = useCol(gridColumns, onActionClick);

  return (
    <>
      <div className="Main contractmanagementdetails">
        {mode === "Default" ? (
          <>
            <div className="content">
              <div className="content-head">
                <HeaderPage pageProps={pageProps} headerConfig={headerConfig} />
              </div>
              <div className="content-body">
                <>
                  <ul className="Filter-links" style={{ gap: "10px" }}>
                    <div className="k-form-field-wrap-popup valueDropdown" ref={divRef}>
                      <Label>Hospital</Label>
                      <MultiSelect
                        name="hospitalDataValue"
                        className="k-multiselect-wrap contract-management-multiselect"
                        dataItemKey="id"
                        textField="text"
                        data={hospitalDataFields.hospitalData}
                        filterable
                        value={hospitalDataFields.hospitalDataValue}
                        autoClose={false}
                        filter={hospitalDataFields.filterValue}
                        loading={hospitalDataFields.isHospitalDataLoading}
                        disabled={hospitalDataFields.isHospitalDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setHospitalDataFields,
                            originalHospitalData,
                            "hospitalData",
                            hospitalDataFields,
                            hospitalDataFields.hospitalDataValue
                          )
                        }
                        onChange={(e) =>
                          handleChangeDropdownValue(
                            e,
                            "hospitalDataValue",
                            setHospitalDataFields,
                            originalHospitalData,
                            hospitalDataFields.hospitalDataValue,
                            hospitalDataFields
                          )
                        }
                        onFilterChange={(e) =>
                          filterChange(e, setHospitalDataFields, originalHospitalData, "hospitalData")
                        }
                        onBlur={() =>
                          setHospitalDataFields((prev) => ({
                            ...prev,
                            hospitalData: originalHospitalData,
                            filterValue: "",
                            search: "",
                          }))
                        }
                      />
                    </div>
                    <div className="k-form-field-wrap-popup valueDropdown">
                      <Label>Specialty</Label>
                      <MultiSelect
                        name="specialtyDataValue"
                        className="k-multiselect-wrap contract-management-multiselect"
                        dataItemKey="id"
                        textField="text"
                        data={specialtyDataFields.specialtyData}
                        filterable
                        value={specialtyDataFields.specialtyDataValue}
                        autoClose={false}
                        filter={specialtyDataFields.filterValue}
                        loading={specialtyDataFields.isSpecialtyDataLoading}
                        disabled={specialtyDataFields.isSpecialtyDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setSpecialtyDataFields,
                            originalSpecialtyData,
                            "specialtyData",
                            specialtyDataFields,
                            specialtyDataFields.specialtyDataValue
                          )
                        }
                        onChange={(e) =>
                          handleChangeDropdownValue(
                            e,
                            "specialtyDataValue",
                            setSpecialtyDataFields,
                            originalSpecialtyData,
                            specialtyDataFields.specialtyDataValue,
                            specialtyDataFields
                          )
                        }
                        onFilterChange={(e) =>
                          filterChange(e, setSpecialtyDataFields, originalSpecialtyData, "specialtyData")
                        }
                        onBlur={() =>
                          setSpecialtyDataFields((prev) => ({
                            ...prev,
                            specialtyData: originalSpecialtyData,
                            filterValue: "",
                            search: "",
                          }))
                        }
                      />
                    </div>
                    <div className="k-form-field-wrap-popup valueDropdown">
                      <Label>Contract Type</Label>
                      <MultiSelect
                        name="contractTypeDataValue"
                        className="k-multiselect-wrap contract-management-multiselect"
                        dataItemKey="id"
                        textField="text"
                        data={contractTypeDataFields.contractTypeData}
                        filterable
                        value={contractTypeDataFields.contractTypeDataValue}
                        autoClose={false}
                        filter={contractTypeDataFields.filterValue}
                        loading={contractTypeDataFields.isContractTypeDataLoading}
                        disabled={contractTypeDataFields.isContractTypeDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setContractTypeDataFields,
                            originalContractTypeData,
                            "contractTypeData",
                            contractTypeDataFields,
                            contractTypeDataFields.contractTypeDataValue
                          )
                        }
                        onChange={(e) =>
                          handleChangeDropdownValue(
                            e,
                            "contractTypeDataValue",
                            setContractTypeDataFields,
                            originalContractTypeData,
                            contractTypeDataFields.contractTypeDataValue,
                            contractTypeDataFields
                          )
                        }
                        onFilterChange={(e) =>
                          filterChange(e, setContractTypeDataFields, originalContractTypeData, "contractTypeData")
                        }
                        onBlur={() =>
                          setContractTypeDataFields((prev) => ({
                            ...prev,
                            contractTypeData: originalContractTypeData,
                            filterValue: "",
                            search: "",
                          }))
                        }
                      />
                    </div>
                    <div className="k-form-field-wrap-popup valueDropdown">
                      <Label>Contract Name</Label>
                      <MultiSelect
                        name="contractNameDataValue"
                        className="k-multiselect-wrap contract-management-multiselect"
                        dataItemKey="id"
                        textField="text"
                        data={contractNameDataFields.contractNameData}
                        filterable
                        value={contractNameDataFields.contractNameDataValue}
                        autoClose={false}
                        filter={contractNameDataFields.filterValue}
                        loading={contractNameDataFields.isContractNameDataLoading}
                        disabled={contractNameDataFields.isContractNameDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setContractNameDataFields,
                            originalContractNameData,
                            "contractNameData",
                            contractNameDataFields,
                            contractNameDataFields.contractNameDataValue
                          )
                        }
                        onChange={(e) =>
                          handleChangeDropdownValue(
                            e,
                            "contractNameDataValue",
                            setContractNameDataFields,
                            originalContractNameData,
                            contractNameDataFields.contractNameDataValue,
                            contractNameDataFields
                          )
                        }
                        onFilterChange={(e) =>
                          filterChange(e, setContractNameDataFields, originalContractNameData, "contractNameData")
                        }
                        onBlur={() =>
                          setContractNameDataFields((prev) => ({
                            ...prev,
                            contractNameData: originalContractNameData,
                            filterValue: "",
                            search: "",
                          }))
                        }
                      />
                    </div>
                    <div className="k-form-field-wrap-popup valueDropdown">
                      <Label>Physician NPI</Label>
                      <MultiSelect
                        name="npiDataValue"
                        className="k-multiselect-wrap contract-management-multiselect"
                        dataItemKey="id"
                        textField="text"
                        data={npiDataFields.npiData}
                        filterable
                        value={npiDataFields.npiDataValue}
                        autoClose={false}
                        filter={npiDataFields.filterValue}
                        loading={npiDataFields.isNpiDataLoading}
                        disabled={npiDataFields.isNpiDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setNpiDataFields,
                            originalNpiData,
                            "npiData",
                            npiDataFields,
                            npiDataFields.npiDataValue
                          )
                        }
                        onChange={(e) => {
                          handleChangeDropdownValue(
                            e,
                            "npiDataValue",
                            setNpiDataFields,
                            originalNpiData,
                            npiDataFields.npiDataValue,
                            npiDataFields
                          );
                          // Auto-select corresponding positions
                          handleNpiChange(e.value);
                        }}
                        onFilterChange={(e) => filterChange(e, setNpiDataFields, originalNpiData, "npiData")}
                        onBlur={() =>
                          setNpiDataFields((prev) => ({
                            ...prev,
                            npiData: originalNpiData,
                            filterValue: "",
                            search: "",
                          }))
                        }
                      />
                    </div>
                    <div className="k-form-field-wrap-popup valueDropdown">
                      <Label>Position Name</Label>
                      <MultiSelect
                        name="positionNameDataValue"
                        className="k-multiselect-wrap contract-management-multiselect"
                        dataItemKey="id"
                        textField="text"
                        data={positionNameDataFields.positionNameData}
                        filterable
                        value={positionNameDataFields.positionNameDataValue}
                        autoClose={false}
                        filter={positionNameDataFields.filterValue}
                        loading={positionNameDataFields.isPositionNameDataLoading}
                        disabled={positionNameDataFields.isPositionNameDataLoading}
                        onOpen={() =>
                          onOpenChange(
                            setPositionNameDataFields,
                            originalPositionNameData,
                            "positionNameData",
                            positionNameDataFields,
                            positionNameDataFields.positionNameDataValue
                          )
                        }
                        onChange={(e) => {
                          handleChangeDropdownValue(
                            e,
                            "positionNameDataValue",
                            setPositionNameDataFields,
                            originalPositionNameData,
                            positionNameDataFields.positionNameDataValue,
                            positionNameDataFields
                          );
                          // Auto-select corresponding NPIs
                          handlePositionChange(e.value);
                        }}
                        onFilterChange={(e) =>
                          filterChange(e, setPositionNameDataFields, originalPositionNameData, "positionNameData")
                        }
                        onBlur={() =>
                          setPositionNameDataFields((prev) => ({
                            ...prev,
                            positionNameData: originalPositionNameData,
                            filterValue: "",
                            search: "",
                          }))
                        }
                      />
                    </div>
                    <Tooltip anchorElement="target" position="left" openDelay={100}>
                      <Button
                        primary
                        className="searchbtn"
                        disabled={
                          hospitalDataFields.isHospitalDataLoading ||
                          specialtyDataFields.isSpecialtyDataLoading ||
                          contractTypeDataFields.isContractTypeDataLoading ||
                          contractNameDataFields.isContractNameDataLoading ||
                          npiDataFields.isNpiDataLoading ||
                          positionNameDataFields.isPositionNameDataLoading
                        }
                        type="submit"
                        title="Fetch Contract Details"
                        onClick={onSearchClick}
                      >
                        <i className="fas fa-search"></i>
                      </Button>
                    </Tooltip>
                  </ul>
                  <div className="dtu-file-container padtop15" ref={_ref}>
                    {initialLoading ? (
                      <LoadingPanel />
                    ) : (
                      <CompData
                        isAPICalling={isAPICalling}
                        initialLoading={true}
                        data={gridData}
                        pageData={pageData}
                        dataCount={memoizedData.length}
                        customColumn={CustomColumn}
                        columns={gridColumns}
                        dataItemKey={DATA_ITEM_KEY}
                        width={"auto"}
                        defaultPageSize={defaultPageSize}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        noDataFound={noDataFound}
                        isExportDataClick={exportDataClick}
                        setExportData={setExportData}
                        exportData={exportData}
                        fileName="Export_ContractManagementDetails"
                        reorderable={!!pageProps.writeAccess}
                        initialFilter={filterField}
                        initialSort={sortField}
                        resetSort={resetSort}
                        handleColumnMenu={handleColumnMenu}
                        rowRender={customRowRender}
                        onRowClick={(e) => e.nativeEvent.stopPropagation()}
                        columnReorderData={columnReorderData}
                        onSelectionChange={onSelectionChange}
                        getExportData={getExportData}
                        onClearFilter={onClearFilter}
                        handlePageChange={handlePageChange}
                        refreshClick={onRefreshClick}
                        id={"contract-management-details"}
                        isSelection={isSelection}
                        isDTUUploadReload={isDTUUploadReload}
                        pageDatavs={pageDatavs}
                        module={"contract-management"}
                        defaultPageSizevs={defaultPageSizevs}
                        originalDataCount={dataCount}
                        originalData={data}
                        navigatable={false}
                        onActionClick={onActionClick}
                        className="contract-management-details-table"
                      />
                    )}
                  </div>
                </>
              </div>
            </div>
          </>
        ) : mode === "Add" ? (
          <ContractAdd pageProps={pageProps} setMode={setMode} onAddContract={handleAddContract} />
        ) : mode === "Edit" ? (
          <UpdateContract
            pageProps={pageProps}
            editData={editData.data}
            setMode={setMode}
            setIsUpdate={setIsUpdate}
            onUpdateContract={handleUpdateContract}
          />
        ) : (
          false
        )}
      </div>
      {isShow.dtuUpload && (
        <UploadDtu
          visible={isShow}
          setShow={setShow}
          title={"Upload Contract Management Details"}
          pageId={"contract_management_details"}
          setAPICall={onRefreshOnManualUpload}
        />
      )}
      <DialogComponent
        title="Delete"
        onCancelConfirm={() => setShow({ ...isShow, deleteConfirm: false })}
        width={"450"}
        height={"auto"}
        className={"Dialog-Delete"}
        desc="Are you sure you want to delete this record?"
        visible={isShow.deleteConfirm}
        actionBtn={[
          {
            text: "Cancel",
            className: "cancelbtn",
            onClick: () => setShow({ ...isShow, deleteConfirm: false }),
          },
          {
            text: "Delete",
            className: "deletebtn",
            onClick: () => {
              // Handle delete logic here
              setShow({ ...isShow, deleteConfirm: false });
            },
          },
        ]}
        titleClassName="delete-confirm"
      />
    </>
  );
};

export default ContractManagementDetails;
